import { MissingContactInfoCard } from '@components/Booking/MissingContactInfoCard';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiCircularLoaderWithTrans } from '@components/Loading/NoddiCircularLoaderWithTrans';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiBasicCard, NoddiButton, noddiToast } from 'noddi-ui-web';
import { RegNumberSearch } from 'noddi-web';
import { useParams } from 'react-router-dom';
import { DinteroSessionPaymentForm } from '../../components/Payment/DinteroSessionPaymentForm';
import { useIsPaymentSuccessful } from '../../hooks/useIsPaymentSuccessful';
import { useAddressFallback, useAddressFallbackActions } from '../../stores/addressFallbackStore';
import {
  useContactFallbackActions,
  useContactFallbackEmail,
  useContactFallbackPhoneNumber
} from '../../stores/contactFallbackStore';
import { getCommonTranslations } from '../../utils/commonTranslations';
import { PointOfSalePaymentOrderDetailsCard } from './components/PointOfSalePaymentOrderDetailsCard';
import { PointOfSalePaymentOrderLookupLoading } from './components/PointOfSalePaymentOrderLookupLoading';

export const PointOfSalePayment = () => {
  const { serviceDepartmentId } = useParams();
  const { isPaymentSuccessful } = useIsPaymentSuccessful();
  const addressFallback = useAddressFallback();
  const { reset: resetAddressFallback } = useAddressFallbackActions();
  const contactEmail = useContactFallbackEmail();
  const contactPhoneNumber = useContactFallbackPhoneNumber();
  const { reset: resetContactFallback } = useContactFallbackActions();

  const {
    mutateAsync: resolveOrder,
    data: orderData,
    isPending: isOrderPending,
    error: orderError
  } = noddiAsync.usePost({
    type: URLKeys.postPosOrderFromPlate
  });

  const order = orderData?.data;
  const bookingSlug = order?.bookings[0]?.slug;

  const { data: booking, error: bookingError } = noddiAsync.useGet({
    type: URLKeys.getBookingInfo,
    input: { slug: bookingSlug! },
    queryConfig: { enabled: !!bookingSlug }
  });

  const {
    mutateAsync: createPaymentSession,
    data: paymentData,
    isPending: isPaymentPending
  } = noddiAsync.usePost({
    type: URLKeys.postPaymentPos,
    queryConfig: {
      onError: () => {
        noddiToast.error([t`Error creating payment session`, t`Please try again`].join(': '));
      }
    }
  });

  const isPaymentSessionStep = !!paymentData;
  if (isPaymentSuccessful) {
    return (
      <ContentWrapperCustomerApp hideBackButton className='flex min-h-[60vh] items-center justify-center'>
        <NoddiBasicCard className='flex w-full max-w-lg flex-col items-center gap-4 text-center'>
          <span className='text-2xl font-bold text-primary'>
            <Trans>Payment successful</Trans>
          </span>
          <span>
            <Trans>Thank you for your payment!</Trans>
          </span>
        </NoddiBasicCard>
      </ContentWrapperCustomerApp>
    );
  }

  const handleResolveOrder = async (plate: string) => {
    resetAddressFallback();
    resetContactFallback();
    await resolveOrder({
      serviceDepartmentId: Number.parseInt(serviceDepartmentId!),
      licensePlate: plate
    });
  };

  const isMissingInvoiceAddress = !!booking && !booking.userGroupAddress;
  const hasFallbackAddress =
    !!addressFallback &&
    addressFallback.address.trim().length > 0 &&
    addressFallback.zipcode.trim().length > 0 &&
    addressFallback.city.trim().length > 0;

  const isMissingEmail = !!booking && !booking.user.email;
  const isMissingPhoneNumber = !!booking && !booking.user.phoneNumber;
  const trimmedEmail = contactEmail.trim();
  const trimmedPhoneNumber = contactPhoneNumber.trim();
  const hasFallbackEmail = trimmedEmail.length > 0;
  const hasFallbackPhoneNumber = trimmedPhoneNumber.length > 0;

  const isContactBlocked = (isMissingEmail && !hasFallbackEmail) || (isMissingPhoneNumber && !hasFallbackPhoneNumber);

  const handleCreatePaymentSession = async () => {
    if (!order) {
      return;
    }
    await createPaymentSession({
      orderId: order.id,
      serviceDepartmentId: Number.parseInt(serviceDepartmentId!),
      addressFallback: isMissingInvoiceAddress && hasFallbackAddress ? addressFallback : null,
      email: isMissingEmail && hasFallbackEmail ? trimmedEmail : null,
      phoneNumber: isMissingPhoneNumber && hasFallbackPhoneNumber ? trimmedPhoneNumber : null
    });
  };

  if (isPaymentSessionStep) {
    return (
      <ContentWrapperCustomerApp hideBackButton>
        {isPaymentPending && <NoddiCircularLoaderWithTrans />}
        {paymentData && (
          <DinteroSessionPaymentForm
            sessionId={paymentData.data.sessionId}
            countryCode={paymentData.data.countryCode as 'NO' | 'SE'}
          />
        )}
      </ContentWrapperCustomerApp>
    );
  }

  return (
    <ContentWrapperCustomerApp hideBackButton>
      <div className='flex flex-col gap-5'>
        <h1 className='text-2xl font-bold text-primary'>
          <Trans>Pay for service</Trans>
        </h1>

        <RegNumberSearch
          translations={getCommonTranslations().getRegNumberText()}
          // oxlint-disable-next-line typescript/no-misused-promises -- async addCar runs handleResolveOrder(); React discards the returned promise, so a rejection is unhandled unless the body catches
          addCar={async ({ licensePlate: plate }) => {
            await handleResolveOrder(plate.number);
          }}
        />

        {isOrderPending && <PointOfSalePaymentOrderLookupLoading />}

        {orderError && <ApiErrorPageWithTrans error={orderError} />}
        {bookingError && <ApiErrorPageWithTrans error={bookingError} />}

        {order && !paymentData && <PointOfSalePaymentOrderDetailsCard order={order} booking={booking} />}

        {order && (isMissingEmail || isMissingPhoneNumber) && (
          <MissingContactInfoCard showEmail={isMissingEmail} showPhone={isMissingPhoneNumber} />
        )}

        {order && !paymentData && order.amountOutstanding.amount > 0 && (
          <NoddiButton
            className='w-full'
            loading={isPaymentPending}
            disabled={(isMissingInvoiceAddress && !hasFallbackAddress) || isContactBlocked}
            // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs handleCreatePaymentSession(); React discards the returned promise, so a rejection is unhandled unless the body catches
            onClick={handleCreatePaymentSession}
          >
            <Trans>Proceed to payment</Trans>
          </NoddiButton>
        )}
      </div>
    </ContentWrapperCustomerApp>
  );
};
